import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { AspectRatioWrapper, IconClose, IconArrowHead, ResponsiveImage } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MAX_PORTRAIT_ASPECT_RATIO = 4 / 3;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MAX_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 4, aspectHeight: 3 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(images?.[0], imageVariants[0]);
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
      index: i,
    };
  });

  // react-image-gallery 1.3 has no prop for the thumbnail button names («Go to Slide N») or the
  // thumbnail nav name («Thumbnail Navigation»), so they are set after render, inside this
  // gallery only (A3-01). React leaves them alone: its own values for these props never change.
  const galleryRef = useRef(null);
  const thumbnailLabels = items.map((item, i) =>
    intl.formatMessage(
      { id: 'ListingImageGallery.thumbnailLabel' },
      { index: i + 1, count: items.length }
    )
  );
  const thumbnailsNavLabel = intl.formatMessage({ id: 'ListingImageGallery.thumbnailsNavLabel' });
  useEffect(() => {
    const root = galleryRef.current?.imageGallery?.current;
    if (!root) {
      return;
    }
    root.querySelectorAll('.image-gallery-thumbnail').forEach((thumb, i) => {
      if (thumbnailLabels[i]) {
        thumb.setAttribute('aria-label', thumbnailLabels[i]);
      }
    });
    root
      .querySelectorAll('.image-gallery-thumbnails-container')
      .forEach(nav => nav.setAttribute('aria-label', thumbnailsNavLabel));
    // A3-08: the library makes its whole root aria-live, controls included; only the slides
    // announce a change (fullscreen button, arrows and thumbnails stay out of the live region)
    root.setAttribute('aria-live', 'off');
    root
      .querySelectorAll('.image-gallery-slides')
      .forEach(slides => slides.setAttribute('aria-live', 'polite'));
  });

  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };
  const renderItem = item => {
    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...imageSizesMaybe}
            // A3-04: the first photo is the LCP candidate; the others load when needed
            {...(item.index === 0 ? { fetchpriority: 'high' } : { loading: 'lazy' })}
          />
        </div>
      </AspectRatioWrapper>
    );
  };
  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
          loading="lazy"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.previousPhoto' })}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        onClick={onClick}
        aria-label={intl.formatMessage({ id: 'ListingImageGallery.nextPhoto' })}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  // #33: focus follows the fullscreen view. The keys make each switch mount the other button anew:
  // the close button takes the focus on open, the opener gets it back on exit (close button, Esc or
  // the browser's own exit), but not on the page's first render. The close button is a plain
  // <button>: the shared Button is disabled on its first render, so it cannot take the focus.
  const hasBeenFullscreen = useRef(false);
  const hideCloseIcon = button => button?.querySelector('svg')?.setAttribute('aria-hidden', 'true');
  const renderFullscreenButton = (onClick, isFullscreen) => {
    if (isFullscreen) {
      hasBeenFullscreen.current = true;
    }
    return isFullscreen ? (
      <button
        key="close"
        // the close icon is decorative: the button is named by its text
        ref={hideCloseIcon}
        className={css.close}
        onClick={onClick}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
        autoFocus
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </button>
    ) : (
      <button
        key="open"
        className={css.openFullscreen}
        onClick={onClick}
        autoFocus={hasBeenFullscreen.current}
      >
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <ReactImageGallery
      ref={galleryRef}
      additionalClass={classes}
      items={items}
      renderItem={renderItem}
      renderThumbInner={renderThumbInner}
      onScreenChange={onScreenChange}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};

export default ListingImageGallery;
